import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Box, Tooltip, Typography, Loader } from "@strapi/design-system"
import { InfoIcon } from "lucide-react"
import { Errors } from "pages/Products/ProductPage/Errors"
import { compressImageWithLoading } from "utils/imageCompression"
import { COMPRESSION_CONFIG } from "../../config/compressionConfig"

interface ImageUploadProps {
  onFileChange: (file: File) => void
  errors?: string[]
  maxSizeBytes?: number
  allowedMimeTypes?: string[]
}

// File validation utility
const validateFile = (
  file: File,
  options: { maxSizeBytes?: number; allowedMimeTypes?: string[] } = {}
) => {
  const {
    maxSizeBytes = 10 * 1024 * 1024,
    allowedMimeTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif"],
  } = options

  // Check file size
  if (file.size > maxSizeBytes)
    return {
      isValid: false,
      error: `File size exceeds maximum allowed size of ${Math.round(
        maxSizeBytes / 1024 / 1024
      )}MB`,
    }

  // Check MIME type
  if (!allowedMimeTypes.includes(file.type))
    return {
      isValid: false,
      error: `Invalid file type. Allowed types: ${allowedMimeTypes.join(", ")}`,
    }

  // Check file extension
  const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf("."))
  const allowedExtensions = [".jpg", ".jpeg", ".png", ".webp", ".gif"]
  if (!allowedExtensions.includes(fileExtension))
    return {
      isValid: false,
      error: `Invalid file extension. Allowed extensions: ${allowedExtensions.join(", ")}`,
    }

  // Check for suspicious file names
  const suspiciousPatterns = [
    /\.exe$/i,
    /\.bat$/i,
    /\.cmd$/i,
    /\.scr$/i,
    /\.pif$/i,
    /\.com$/i,
    /\.vbs$/i,
    /\.js$/i,
    /\.jar$/i,
    /\.php$/i,
    /\.asp$/i,
    /\.aspx$/i,
    /\.jsp$/i,
  ]

  if (suspiciousPatterns.some((pattern) => pattern.test(file.name)))
    return {
      isValid: false,
      error: "File type not allowed for security reasons",
    }

  // Check for null bytes or other suspicious characters
  if (file.name.includes("\0") || file.name.includes("\x00"))
    return {
      isValid: false,
      error: "Invalid file name characters detected",
    }

  return { isValid: true }
}

// Sanitize file name
const sanitizeFileName = (fileName: string): string => {
  return (
    fileName
      // eslint-disable-next-line no-control-regex
      .replace(/[<>:"/\\|?*\u0000-\u001f]/g, "_")
      .replace(/^\.+/, "")
      .replace(/\.+$/, "")
      .trim()
      .substring(0, 255)
  )
}

export const ImageUpload = ({
  onFileChange,
  errors = [],
  maxSizeBytes,
  allowedMimeTypes,
}: ImageUploadProps) => {
  const { t } = useTranslation()
  const [isCompressing, setIsCompressing] = useState(false)
  const [compressionProgress, setCompressionProgress] = useState(0)
  const [validationError, setValidationError] = useState<string | null>(null)

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Clear previous validation errors
    setValidationError(null)

    // Validate file before processing
    const validation = validateFile(file, {
      maxSizeBytes,
      allowedMimeTypes,
    })

    if (!validation.isValid) {
      setValidationError(validation.error || "Invalid file")
      e.target.value = ""
      return
    }

    // Sanitize file name
    const sanitizedFile = new File([file], sanitizeFileName(file.name), {
      type: file.type,
      lastModified: file.lastModified,
    })

    setIsCompressing(true)
    setCompressionProgress(0)

    try {
      const result = await compressImageWithLoading(
        sanitizedFile,
        (progress) => setCompressionProgress(progress),
        COMPRESSION_CONFIG
      )

      onFileChange(result.compressedFile)
    } catch (error) {
      setValidationError("Failed to process image. Please try again.")
      onFileChange(sanitizedFile)
    } finally {
      setIsCompressing(false)
      setCompressionProgress(0)
      e.target.value = ""
    }
  }

  return (
    <Box style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <label htmlFor="productImageUrl">
        <Box
          style={{
            gap: 4,
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "start",
          }}
        >
          <Typography
            variant="pi"
            style={{
              textAlign: "center",
              fontWeight: "600",
              fontSize: "12px",
              height: "12px",
              lineHeight: "12px",
            }}
          >
            {t("imageUpload.productImageLabel")}
          </Typography>
          <Tooltip description={t("imageUpload.productImageTooltip")}>
            <Box
              style={{
                gap: 4,
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <InfoIcon
                style={{ width: "16px", height: "16px", cursor: "pointer", color: "white" }}
              />
            </Box>
          </Tooltip>
        </Box>
      </label>
      <input
        type="file"
        id="productImageUrl"
        name="productImageUrl"
        onChange={handleFileChange}
        accept="image/*"
        style={{ display: "none" }}
        disabled={isCompressing}
      />

      <label
        htmlFor="productImageUrl"
        style={{
          border: "1px solid #DCDCE3",
          padding: 8,
          borderRadius: 4,
          textAlign: "center",
          cursor: isCompressing ? "not-allowed" : "pointer",
          opacity: isCompressing ? 0.6 : 1,
        }}
      >
        {isCompressing ? (
          <Box style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <Loader small />
            <Typography
              style={{
                fontWeight: "600",
                fontSize: "12px",
                lineHeight: "12px",
              }}
            >
              {t("imageUpload.compressing")} {compressionProgress}%
            </Typography>
          </Box>
        ) : (
          <Typography
            style={{
              fontWeight: "600",
              fontSize: "12px",
              lineHeight: "12px",
            }}
          >
            {t("imageUpload.uploadImage")}
          </Typography>
        )}
      </label>
      <Errors _errors={[...errors, ...(validationError ? [validationError] : [])]} />
    </Box>
  )
}
